import { ControlledInput } from '../../molecules/input'
import { ControlledRating } from '../../molecules/rating'
import { ControlledTextArea } from '../../molecules/text-area'
import { DialogStyled, TitleDialog, DescriptionDialog, RatingWrapper } from './dialog-review.styled'
import Form from '@bluheadless/ui-logic/src/components/form/form'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { useProductReviewAdd } from '@bluheadless/ui-logic/src/hooks/product/useProductReviewAdd'
import { useCustomer } from '@bluheadless/ui-logic/src/hooks/customer/useCustomer'
import { useUser } from '@bluheadless/ui-logic/src/providers/user'
import { Grid } from '@mui/material'
import SkeletonList from '../../molecules/skeleton/list/skeleton-list'
import { yupResolver } from '@hookform/resolvers/yup'
import { object as yupObject, string as yupString } from 'yup'
import SubmitButton from '../../molecules/button/submit-button'
import { bool, number, func } from 'prop-types'
import { useIntl } from 'react-intl'
import Typography from '../../atoms/typography'

const DialogReview = ({ open, onSubmit, onClose, productId, ...props }) => {
	const { enqueueNotification } = useNotification()
	const { formatMessage } = useIntl()
	const { loading: loadingCustomer, firstname } = useCustomer()
	const { action: addReview } = useProductReviewAdd()
	const [{ isLoggedIn }] = useUser()

	const defaultValues = {
		rating: '',
		reviewUsername: '',
		reviewTitle: '',
		reviewDescription: '',
	}

	const validationSchema = yupObject().shape(
		{
			rating: yupString()
				.required()
				.label(formatMessage({ id: 'form_field_rating' })),
			reviewTitle: yupString()
				.required()
				.isAlpha()
				.label(formatMessage({ id: 'form_field_review_title' })),
			reviewUsername: !isLoggedIn
				? yupString()
						.required()
						.isAlpha()
						.label(formatMessage({ id: 'form_field_review_username' }))
				: null,
			reviewDescription: yupString()
				.required()
				.isAlpha()
				.label(formatMessage({ id: 'form_field_review_description' })),
		},
		[]
	)

	const handleSubmit = async (formData) => {
		try {
			await addReview({
				title: formData.reviewTitle,
				description: formData.reviewDescription,
				user: firstname || formData.reviewUsername,
				value: parseInt(formData.rating),
				productId: parseInt(productId),
			})
			enqueueNotification({ uiProps: { message: formatMessage({ id: 'review_submit_success' }) } })
			onSubmit ? onSubmit(formData) : null
		} catch (err) {
			enqueueNotification({ uiProps: { severity: 'error', message: formatMessage({ id: 'an_error_occurred' }) } })
		}
	}

	return (
		<DialogStyled open={open} onClose={() => (onClose ? onClose() : null)} showButtonClose variant="md" {...props}>
			<TitleDialog gutterBottom variant="h3">
				{formatMessage({ id: 'review_dialog_title' })}
			</TitleDialog>
			<DescriptionDialog variant="subtitle1" color="grey.main">
				{formatMessage({ id: 'review_dialog_description' })}
			</DescriptionDialog>
			<Form
				resetOnSubmitSuccess
				onSubmit={handleSubmit}
				options={{
					resolver: yupResolver(validationSchema),
					defaultValues: defaultValues,
				}}
			>
				<RatingWrapper>
					<ControlledRating name="rating" required size="large" />
				</RatingWrapper>
				{isLoggedIn ? (
					<Typography component="div" variant="body1" color="grey.main" mb={4} display="flex" flexDirection="column">
						<Typography component="span" variant="caption">
							{formatMessage({ id: 'form_field_review_username' })}
						</Typography>
						<Typography component="span" color="primary.main">
							{firstname}
						</Typography>
						{loadingCustomer ? (
							<SkeletonList
								rows={1}
								skeletonProps={{
									width: 100,
									height: 20,
								}}
							/>
						) : null}
					</Typography>
				) : (
					<ControlledInput name="reviewUsername" required label={formatMessage({ id: 'review_form_username' })} />
				)}
				<ControlledInput name="reviewTitle" required label={formatMessage({ id: 'review_form_title' })} />
				<ControlledTextArea
					name="reviewDescription"
					required
					label={formatMessage({ id: 'review_form_description' })}
					helperText={formatMessage({ id: 'forms_file_helper_text' })}
				/>
				<Grid container mt={2} display="flex" justifyContent="flex-end">
					<Grid item xs={12} sm={6}>
						<SubmitButton color="primary" fullWidth label={formatMessage({ id: 'submit' })} />
					</Grid>
				</Grid>
			</Form>
		</DialogStyled>
	)
}

DialogReview.propTypes = {
	open: bool,
	onSubmit: func,
	onClose: func,
	productId: number,
}

export default DialogReview
